import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, Check } from "lucide-react";
import { PRODUCTS } from "../data/products";
import { QuickCheckout } from "./QuickCheckout";
import { useCart } from "../context/CartContext";
import { useState } from "react";

const ProductRow = ({ product, index }) => {
  const { add } = useCart();
  const [added, setAdded] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const reversed = index % 2 === 1;

  const addToBag = () => {
    add(product.id);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-6 items-center ${
        index > 0 ? "mt-32 md:mt-48" : "mt-20"
      }`}
      data-testid={`product-row-${product.id}`}
    >
      <div className={`md:col-span-7 ${reversed ? "md:order-2 md:col-start-6" : ""}`}>
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`}>
          <motion.div
            whileHover={{ scale: 0.97 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="group relative overflow-hidden border border-white/5 hover:border-gold/40 transition-colors duration-700"
          >
            <img
              src={product.image}
              alt={product.alt}
              className="w-full max-h-[80vh] object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          </motion.div>
        </Link>
      </div>

      <div
        className={`md:col-span-4 ${
          reversed ? "md:order-1 md:col-start-1 md:text-right md:justify-self-end" : "md:col-start-9"
        }`}
      >
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{product.edition}</p>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-5xl md:text-6xl font-light text-white leading-none hover:text-gold transition-colors duration-500">
            {product.name}
          </h3>
        </Link>
        <p className="mt-6 text-sm md:text-base text-ash leading-relaxed max-w-xs md:max-w-none md:inline-block">
          {product.description}
        </p>
        <div className={`mt-8 flex flex-wrap items-baseline gap-4 ${reversed ? "md:justify-end" : ""}`}>
          <p className="font-serif text-3xl text-champagne" data-testid={`product-price-${product.id}`}>
            ${product.price}
          </p>
          <p className="text-base text-ash/70 line-through" data-testid={`product-compare-at-${product.id}`}>
            ${product.compareAt}
          </p>
          <span className="text-[10px] uppercase tracking-[0.25em] text-gold border border-gold/40 rounded-full px-3 py-1">
            Launch Offer
          </span>
        </div>
        <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-ash/60" data-testid={`launch-ends-${product.id}`}>
          Launch pricing ends soon
        </p>
        <div className={`mt-6 flex flex-wrap items-center gap-4 ${reversed ? "md:justify-end" : ""}`}>
          <button
            onClick={() => setShowPay(true)}
            className="group relative inline-flex items-center gap-2 rounded-full border border-gold/60 px-8 py-3.5 text-xs uppercase tracking-[0.25em] text-gold overflow-hidden transition-colors duration-500 hover:text-ink"
            data-testid={`product-buy-button-${product.id}`}
          >
            <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
            <span className="relative">Acquire</span>
            <ArrowUpRight className="relative h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={addToBag}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ash hover:text-gold transition-colors duration-500"
            data-testid={`product-add-bag-${product.id}`}
          >
            {added ? (
              <>
                <Check className="h-4 w-4 text-gold" /> Added
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" /> Add to Bag
              </>
            )}
          </button>
        </div>
      </div>
      <QuickCheckout
        items={showPay ? [{ lookup_key: product.lookupKey, quantity: 1 }] : null}
        title={`${product.name} — $${product.price}`}
        onClose={() => setShowPay(false)}
      />
    </motion.article>
  );
};

export const Products = () => (
  <section id="collection" className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-48" data-testid="collection-section">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
    >
      <h2 className="font-serif font-light text-5xl md:text-7xl leading-none tracking-tight">
        The <span className="italic gold-text-gradient">Collection</span>
      </h2>
      <p className="text-xs uppercase tracking-[0.3em] text-ash pb-2">
        Five frames — one stance
      </p>
    </motion.div>

    {PRODUCTS.map((p, i) => (
      <ProductRow key={p.id} product={p} index={i} />
    ))}
  </section>
);
